/** @jsxImportSource @emotion/react */
import { css } from "@emotion/react";
import List from "@mui/material/List";
import ListItem from "@mui/material/ListItem";
import ListItemButton from "@mui/material/ListItemButton";
import ListItemText from "@mui/material/ListItemText";
import StarIcon from "../../shared/assets/icons/star.svg";
import { useEffect } from "react";
import { getCustomerServiceDetails } from "./apiRequest";
import { useState } from "react";
import { Box } from "@mui/system";
import {
  Card,
  CardContent,
  CardMedia,
  Tab,
  Tabs,
  Typography,
} from "@mui/material";

import { v4 } from "uuid";
import { useLocation } from "react-router-dom";
import AddCircularIcon from "../../shared/components/reusableComp/AddCircularIcon";
import Markdown from "markdown-to-jsx";

const bannerWrapper = css`
  width: 100%;
  color: white;
  position: relative;
  overflow: hidden;
  img {
    width: 100%;
    height: 100%;
    vertical-align: bottom;
  }

  .MuiTypography-h5 {
    position: absolute;
    top: 50px;
    left: 80px;
  }
  .MuiTypography-body2 {
    position: absolute;
    top: 99px;
    left: 80px;
    width: 36%;
    height: 24%;
    display: -webkit-box;
    overflow: hidden;
    text-overflow: ellipsis;
    -webkit-box-orient: vertical;
    -webkit-line-clamp: 3;
  }
`;

const navWrapper = css`
  display: flex;
  text-align: left;
  background-color: #121212;
  max-height: 100%;
  ul {
    margin-top: 95px;
    margin-left: 55px;
    width: 15%;
  }
  li {
    position: relative;
    font-size: 16px;
    line-height: 36px;

    > div {
      padding: 0;
    }
  }
`;

const navTextStyle = (boolean) => css`
  :before {
    content: "";
    width: 11px;
    left: -25px;
    top: 12px;
    height: 10px;
    position: absolute;
    background-size: cover;
    background-image: url(${boolean && StarIcon});
  }
  .MuiListItemText-root {
    margin: 0;
    span {
      color: ${boolean ? "#fff" : "#979797"};
      font-family: "Arial", sans-serif;
      font-size: 16px;
      line-height: 36px;
    }
`;

const cardContentStyle = css`
  .MuiTypography-h5 {
    position: absolute;
    top: 8%;
    left: 6%;
  }
  .MuiTypography-h6 {
    position: absolute;
    top: 34%;
    left: 6%;
    width: 38%;
    height: 35%;
    display: -webkit-box;
    overflow: hidden;
    text-overflow: ellipsis;
    -webkit-box-orient: vertical;
    -webkit-line-clamp: 3;
    line-height: 1.2;
  }
`;

const containerStyle = (length) => css`
  width: 100%;
  min-height: 62vh;
  display: grid;
  grid-template-rows: ${length < 3 ? "repeat(1, 1fr)" : "repeat(2, 1fr)"};
  grid-template-columns: ${length < 2
    ? "repeat(1,1fr)"
    : length < 5
    ? "repeat(2, 1fr)"
    : "repeat(3,1fr)"};

  .item1 {
    grid-row-start: ${length == 3 ? 1 : "unset"};
    grid-row-end: ${length == 3 ? 3 : "unset"};
    grid-column-start: ${length == 5 ? 1 : "unset"};
    grid-column-end: ${length == 5 ? 3 : "unset"};

    width: ${length === 5 ? "75%" : "unset"};
  }
  .item2 {
    position: ${length === 5 ? "relative" : "unset"};
    right: ${length === 5 ? "50%" : "unset"};
    width: ${length === 5 ? "150%" : "unset"};
  }

  .banner {
    &:hover .banner-image {
      transform: scale(1.05);
      transition: 0.9s ease;
    }
    height: 100%;
    background-color: #9b9b9b;
    position: relative;
    background-size: cover;
    background-repeat: no-repeat;
    background-position: center;
  }
`;

const tabStyle = css`
  margin-bottom: 2px;
`;

export default function CustomerService() {
  let location = useLocation();
  const currentState = location?.state?.id;
  const customerId = location?.state?.customerId;
  const [customerServiceDetails, setCustomerServiceDetails] = useState([]);

  const [navigationCurrentState, setNavigationCurrentState] = useState([]);

  const [hoverState, setHoverState] = useState(-1);
  const [value, setValue] = useState(0);

  useEffect(() => {
    getCustomerServiceDetails().then(({ data }) => {
      setCustomerServiceDetails(data.data);
      const customerIdData = data.data.findIndex(
        (d) => d.attributes.title === customerId
      );
      const customerIdState = customerIdData > -1 ? customerIdData : null;
      setNavigationCurrentState(
        data.data[currentState || customerIdState || 0].attributes
      );
    });
  }, []);

  useEffect(() => {
    document.title = navigationCurrentState?.title;
    setValue(currentState);
  }, [navigationCurrentState]);

  useEffect(() => {
    customerServiceDetails.length > 0 &&
      setNavigationCurrentState(
        customerServiceDetails[currentState || 0].attributes
      );
  }, [currentState]);

  const handleChange = (event, newValue) => {
    setValue(newValue);
  };

  const renderCard = (imgUrl, imgName, title, description, buttonLink) => {
    return (
      <Card
        key={v4()}
        sx={{ position: "relative", backgroundColor: "#121200" }}
        onClick={() => (window.location = buttonLink)}
      >
        <Box sx={{ display: "flex", minHeight: "23vh" }}>
          {imgUrl && (
            <CardMedia
              component="img"
              image={`${process.env.REACT_APP_BASEURL}${imgUrl}`}
              alt={imgName}
            />
          )}
          <CardContent css={cardContentStyle}>
            <Typography
              gutterBottom
              variant="h5"
              component="div"
              color="text.secondary"
            >
              {title}
            </Typography>
            <Typography variant="h6" color="text.secondary">
              <Markdown>{description}</Markdown>
            </Typography>
          </CardContent>
        </Box>
      </Card>
    );
  };

  return (
    <>
      <Box
        css={navWrapper}
        sx={{ display: { xs: "none", md: "flex", minHeight: "62vh" } }}
      >
        <List sx={{ backgroundColor: "background.default", width: "20%" }}>
          {customerServiceDetails.map((data) => (
            <ListItem
              key={data?.attributes?.navigationTitle}
              disablePadding
              css={() =>
                navTextStyle(
                  data?.attributes?.navigationTitle ===
                    navigationCurrentState?.navigationTitle
                )
              }
            >
              <ListItemButton>
                <ListItemText
                  primary={data?.attributes?.navigationTitle}
                  onClick={() => setNavigationCurrentState(data?.attributes)}
                />
              </ListItemButton>
            </ListItem>
          ))}
        </List>
        {navigationCurrentState?.serviceTile &&
          navigationCurrentState?.serviceTile.length > 0 && (
            <Box
              css={() =>
                containerStyle(navigationCurrentState?.serviceTile?.length)
              }
            >
              {navigationCurrentState?.serviceTile &&
                navigationCurrentState?.serviceTile.length > 0 &&
                navigationCurrentState?.serviceTile.map((d, i) => (
                  <div
                    css={bannerWrapper}
                    key={v4()}
                    className={`banner item${i + 1}`}
                    onMouseEnter={() => setHoverState(i)}
                    onMouseLeave={() => setHoverState(-1)}
                  >
                    {d?.media?.data?.attributes?.url && (
                      <img
                        width={"80%"}
                        className="banner-image"
                        src={`${process.env.REACT_APP_BASEURL}${d?.media?.data?.attributes?.url}`}
                      />
                    )}
                    <Typography variant="h5">{d?.title}</Typography>
                    <Typography variant="body2" component="div">
                      <Typography variant="h6" key={v4()}>
                        <Markdown>{d?.description || ""}</Markdown>
                      </Typography>
                    </Typography>

                    <AddCircularIcon
                      translateValue={"translate(70px, -70px)"}
                      buttonCss={
                        hoverState === i
                          ? "font-size: medium;"
                          : "visibility: hidden;"
                      }
                      handleClick={() =>
                        d?.isExternalLink
                          ? window.open(d?.buttonLink)
                          : (window.location = d?.buttonLink)
                      }
                      buttonText={d?.buttonText}
                      iconCss={{
                        width: "18px",
                        height: "18px",
                        marginRight: "8px",
                        padding: "4px",
                      }}
                    />
                  </div>
                ))}
            </Box>
          )}
      </Box>
      <Box
        sx={{
          display: { xs: "flex", md: "none" },
          flexDirection: "column",
        }}
      >
        <Tabs
          value={value}
          onChange={handleChange}
          variant="scrollable"
          scrollButtons="auto"
          css={tabStyle}
        >
          {customerServiceDetails.map((item, index) => (
            <Tab
              key={index}
              style={{ color: value === index ? "white" : "#979797" }}
              label={item?.attributes?.navigationTitle}
            ></Tab>
          ))}
        </Tabs>
        {customerServiceDetails &&
          customerServiceDetails[value]?.attributes?.serviceTile?.map(
            (data) => {
              return renderCard(
                data?.media?.data?.attributes?.url,
                data?.media?.data?.attributes?.name,
                data?.title,
                data?.description || "",
                data?.buttonLink
              );
            }
          )}
      </Box>
    </>
  );
}
